/**
 * Fill in the Blanks element grader
 */

import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import size from 'lodash/size';
import trim from 'lodash/trim';
import type {
    ElementGrader,
    GradingConfig,
    GradingResult,
    ValidationResult,
    ValidationError,
} from '@ai-content-hub/content-kit';
import type { FITBElementData, FITBAnswer, FITBBlank } from './schema';
import { countBlanksInTiptap, normalizeFITBData } from './schema';

export const FITB_VALIDATE_PASSAGE_REQUIRED =
    'Phải định nghĩa ít nhất 01 khoảng trống.';
export const FITB_VALIDATE_ACCEPTED_ANSWER_REQUIRED =
    'Phải nhập giá trị đúng cho khoảng trống đó.';
// EAA-2067: invisible / look-alike characters an author's or learner's keyboard can insert
// (Vietnamese IMEs, copy from Word/web) that made a visibly identical answer compare unequal.
const FITB_ZERO_WIDTH_RE = /[\u00AD\u200B-\u200D\u2060\uFEFF]/g;
const FITB_HTML_TAG_RE = /<[^>]*>/g;
const FITB_HTML_ENTITIES: ReadonlyArray<[RegExp, string]> = [
    [/&nbsp;/gi, ' '],
    [/&quot;/gi, '"'],
    [/&(#39|apos);/gi, "'"],
    [/&lt;/gi, '<'],
    [/&gt;/gi, '>'],
    // `&amp;` last so `&amp;nbsp;` decodes once, to the literal text `&nbsp;`.
    [/&amp;/gi, '&'],
];

/**
 * EAA-2067: canonical form used to compare a typed/selected answer with an accepted one.
 * Strips stray markup and entities (answers pasted from rich text), NFC-normalizes (composed
 * vs decomposed Vietnamese diacritics), drops zero-width characters, maps curly quotes to
 * straight ones, collapses every whitespace run (incl. NBSP) to one space and trims.
 * Case folds unless the blank is case sensitive.
 */
export function normalizeFitbAnswerText(text: unknown, caseSensitive: boolean): string {
    // Tags are dropped, not spaced: `go<b>es</b>` is the word "goes".
    let t = String(text ?? '').replace(FITB_HTML_TAG_RE, '');
    forEach(FITB_HTML_ENTITIES, ([re, ch]) => {
        t = t.replace(re, ch);
    });
    t = t
        .normalize('NFC')
        .replace(FITB_ZERO_WIDTH_RE, '')
        .replace(/[\u2018\u2019\u201B\u2032]/g, "'")
        .replace(/[\u201C\u201D\u201F\u2033]/g, '"')
        .replace(/\s+/g, ' ');
    t = trim(t);
    return caseSensitive ? t : t.toLowerCase();
}

function isAnswerCorrectForBlank(blank: FITBBlank, rawAnswer: string | undefined, caseSensitive: boolean): boolean {
    if (rawAnswer == null) return false;
    const normalized = normalizeFitbAnswerText(rawAnswer, caseSensitive);
    if (normalized === '') return false;
    // EAA-2067: compare canonical forms; an accepted answer that is blank once normalized
    // (e.g. only `&nbsp;`) never matches anything.
    const accepted = filter(
        map(blank.acceptedAnswers, (a) => normalizeFitbAnswerText(a, caseSensitive)),
        (a) => a !== ''
    );
    if (size(accepted) === 0) return false;
    return accepted.some((a) => a === normalized);
}

/** Per-blank correctness for review UI when full GradingResult.details is unavailable. */
export function buildFitbPerBlankCorrectness(
    data: FITBElementData,
    answer: FITBAnswer | undefined,
): Record<string, boolean> {
    const caseSensitive = data.gradingCaseSensitive === true;
    const out: Record<string, boolean> = {};
    map(data.blanks, (b: FITBBlank) => {
        out[b.id] = isAnswerCorrectForBlank(b, answer?.[b.id], caseSensitive);
    });
    return out;
}

type FITBGradingInput = GradingConfig & {
    partialCreditEnabled?: boolean;
};

export const fitbGrader: ElementGrader<FITBElementData, FITBAnswer> = {
    grade(data: FITBElementData, answer: FITBAnswer, config: GradingConfig = {}): GradingResult {
        const extended: FITBGradingInput = { ...config };
        const maxPoints = typeof extended.max === 'number' ? extended.max : 1;
        const partialCreditEnabled = extended.partialCreditEnabled;
        const partialCredit =
            partialCreditEnabled !== undefined ? Boolean(partialCreditEnabled) : true;
        const caseSensitive =
            data.gradingCaseSensitive === true;

        const { blanks, explanation } = data;
        const total = size(blanks);
        if (total === 0) {
            return {
                isCorrect: false,
                score: 0,
                max: maxPoints,
                // EAA-2067: no hard-coded English text; ContentPlayer shows its localized
                // t('incorrect'). An item with no blanks is already blocked by validate().
                feedback: undefined,
            };
        }

        let correctCount = 0;
        map(blanks, (b: FITBBlank) => {
            const userVal = answer?.[b.id];
            if (isAnswerCorrectForBlank(b, userVal, caseSensitive)) correctCount += 1;
        });

        const score = partialCredit ? (correctCount / total) * maxPoints : correctCount === total ? maxPoints : 0;
        const rounded = Math.round(score * 100) / 100;
        const isCorrect = correctCount === total;

        return {
            isCorrect,
            score: rounded,
            max: maxPoints,
            // EAA-2067: no hard-coded English feedback ("Correct!" / "You filled X of Y blanks
            // correctly.") — it showed in English on a Vietnamese screen. ContentPlayer falls back
            // to its localized t('correct') / t('incorrect'); the per-blank marks and
            // `details.correctCount` still carry the partial result.
            feedback: undefined,
            correctAnswer: map(blanks, (b) => (b.acceptedAnswers ?? [])[0] ?? ''),
            details: {
                explanation,
                correctCount,
                totalItems: total,
                perBlank: map(blanks, (b) => ({
                    id: b.id,
                    ok: isAnswerCorrectForBlank(b, answer?.[b.id], caseSensitive),
                })),
            },
        };
    },

    getCorrectAnswer(data: FITBElementData): FITBAnswer {
        const out: FITBAnswer = {};
        map(data.blanks, (b: FITBBlank) => {
            out[b.id] = (b.acceptedAnswers ?? [])[0] ?? '';
        });
        return out;
    },

    validate(data: FITBElementData): ValidationResult {
        const errors: ValidationError[] = [];
        const normalized = normalizeFITBData(data);
        const n = countBlanksInTiptap(normalized.passage);
        if (n === 0) {
            errors.push({
                field: 'passage',
                message: FITB_VALIDATE_PASSAGE_REQUIRED,
                code: 'required',
            });
        }
        map(normalized.blanks, (b: FITBBlank, index: number) => {
            const hasAnswer = filter(b.acceptedAnswers, (a) => trim(String(a)) !== '').length > 0;
            if (!hasAnswer) {
                errors.push({
                    field: `blanks.${index}.acceptedAnswers`,
                    message: FITB_VALIDATE_ACCEPTED_ANSWER_REQUIRED,
                    code: 'required',
                });
            }
        });
        return { isValid: size(errors) === 0, errors };
    },

    /**
     * Each blank is 1 gradable question.
     */
    countQuestions(data: FITBElementData): number {
        return (data.blanks ?? []).length;
    },
};

export default fitbGrader;
